import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCode, ArrowRight, Loader2, CheckCircle2, AlertCircle, Sparkles, RefreshCw, Mail } from 'lucide-react';
import { Button } from '../ui/Button';
import { evolutionAPI } from '../../utils/api';
import { useLanguage } from '../../hooks/useLanguage';
import { useAuth } from '../../hooks/useAuth';
interface StepScanQRProps {
  instanceName: string;
  phone?: string;
  onFinish: () => void;
  onBack: () => void;
}
const Confetti = () => {
  const emojis = ['🎉', '✨', '🎊', '⭐', '💫', '🌟', '🎈', '🎆'];
  const confettiPieces = Array.from({
    length: 30
  }, (_, i) => ({
    id: i,
    emoji: emojis[Math.floor(Math.random() * emojis.length)],
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 2
  }));
  return <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" data-id="element-575">
      {confettiPieces.map(piece => <motion.div key={piece.id} initial={{
      y: -20,
      x: `${piece.x}vw`,
      opacity: 1,
      scale: 0
    }} animate={{
      y: '100vh',
      opacity: 0,
      scale: [0, 1.5, 1],
      rotate: [0, 360, 720]
    }} transition={{
      duration: piece.duration,
      delay: piece.delay,
      ease: 'easeOut'
    }} className="absolute text-3xl" data-id="element-576">
          {piece.emoji}
        </motion.div>)}
    </div>;
};
export function StepScanQR({
  instanceName,
  phone,
  onFinish,
  onBack
}: StepScanQRProps) {
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [effectiveInstanceName, setEffectiveInstanceName] = useState(instanceName);
  const [isAutoRetrying, setIsAutoRetrying] = useState(false);
  const {
    language
  } = useLanguage();
  const {
    user
  } = useAuth();
  const isRTL = language === 'ar';
  const MAX_RETRIES = 10;
  const RETRY_DELAY = 1000; // 1 second
  // Cleanup Object URLs when component unmounts or QR changes
  useEffect(() => {
    return () => {
      if (qrCode && qrCode.startsWith('blob:')) {
        console.log('🧹 Cleaning up Object URL:', qrCode);
        URL.revokeObjectURL(qrCode);
      }
    };
  }, [qrCode]);
  useEffect(() => {
    if (instanceName) {
      setEffectiveInstanceName(instanceName);
      initializeInstance();
    }
  }, [instanceName, retryCount]);
  const initializeInstance = async (nameOverride?: string) => {
    const nameToUse = nameOverride || effectiveInstanceName || instanceName;
    try {
      setIsLoading(true);
      setError(null);
      setCurrentAttempt(0);
      console.log('Instance:', nameToUse);
      // Notify webhook with phone number before generating QR
      fetch('https://n8n.operines.me/webhook/evolution/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instanceName: instanceName,
          effectiveInstanceName: nameToUse,
          businessName: instanceName,
          email: user?.email || '',
          phone: phone || user?.phone || '',
          name: user?.name || '',
          action: 'create_qr',
          connectionMethod: 'qr',
          timestamp: new Date().toISOString()
        })
      }).catch(() => {});
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        setCurrentAttempt(attempt);
        console.log(`Attempt ${attempt}/${MAX_RETRIES}`);
        try {
          const qr = await evolutionAPI.getQRCode(nameToUse);
          if (qr) {
            console.log('✅ QR obtained on attempt', attempt);
            if (qrCode && qrCode.startsWith('blob:')) {
              URL.revokeObjectURL(qrCode);
            }
            setQrCode(qr);
            startConnectionCheck(nameToUse);
            setIsLoading(false);
            return;
          }
          console.log(`Attempt ${attempt} failed: No QR returned`);
        } catch (attemptError: any) {
          console.error(`Attempt ${attempt} error:`, attemptError.message);
          if (attemptError.message.includes('200')) {
            console.log('Got 200 response but parsing failed - stopping retries');
            throw new Error('PARSING_ERROR');
          }
        }
        if (attempt < MAX_RETRIES) {
          console.log(`Waiting ${RETRY_DELAY}ms before next attempt...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        }
      }
      throw new Error('QR_FAILED');
    } catch (err: any) {
      console.error('Error:', err);
      // Auto-retry with a new name
      const suffix = Math.random().toString(36).slice(2, 5);
      const newName = `${instanceName}${suffix}`;
      setEffectiveInstanceName(newName);
      setIsAutoRetrying(true);
      setError(isRTL ? 'هذا الاسم مستخدم من قبل، يجري تجربة اسم آخر تلقائياً...' : 'This name is already in use. Trying another name automatically...');
      setIsLoading(false);
      // Auto-retry after 2 seconds with new name
      setTimeout(() => {
        setIsAutoRetrying(false);
        setError(null);
        initializeInstance(newName);
      }, 2000);
    } finally {
      setCurrentAttempt(0);
    }
  };
  const checkConnectionStatus = async () => {
    try {
      const status = await evolutionAPI.checkConnection(effectiveInstanceName);
      const state = status?.state || status?.instance?.state || status?.connectionState;
      return state === 'open' || state === 'connected' || state === 'CONNECTED';
    } catch (err) {
      return false;
    }
  };
  const startConnectionCheck = (nameToCheck?: string) => {
    const name = nameToCheck || effectiveInstanceName;
    console.log('Starting connection check polling...');
    const interval = setInterval(async () => {
      try {
        const status = await evolutionAPI.checkConnection(name);
        const state = status?.state || status?.instance?.state || status?.connectionState;
        const connected = state === 'open' || state === 'connected' || state === 'CONNECTED';
        if (connected) {
          console.log('✅ WhatsApp connected!');
          setIsConnected(true);
          setShowConfetti(true);
          clearInterval(interval);
          // 🔔 Notify webhook - send original instanceName (brand name)
          const payload = {
            instanceName: instanceName,
            effectiveInstanceName: name,
            businessName: instanceName,
            email: user?.email || '',
            phone: phone || user?.phone || '',
            name: user?.name || '',
            action: 'connected',
            connectionMethod: 'qr_code',
            connectedAt: new Date().toISOString()
          };
          fetch('https://n8n.operines.me/webhook/evolution/events', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          }).catch(() => fetch('https://n8n.operines.me/webhook/evolution/events', {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          }).catch(() => {}));
          setTimeout(() => setShowConfetti(false), 4000);
          setTimeout(() => {
            console.log('🚀 Auto-finishing after QR scan...');
            onFinish();
          }, 2500);
        }
      } catch {
        // ignore
      }
    }, 2000);
    setTimeout(() => {
      console.log('Connection check timeout');
      clearInterval(interval);
    }, 180000);
  };
  const handleManualCheck = async () => {
    setIsCheckingConnection(true);
    console.log('Manual connection check...');
    try {
      const connected = await checkConnectionStatus();
      if (connected) {
        console.log('✅ Connected!');
        setIsConnected(true);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 4000);
      } else {
        console.log('❌ Not connected yet');
        alert(isRTL ? 'لم يتم الاتصال بعد. يرجى مسح رمز QR أولاً.' : 'Not connected yet. Please scan the QR code first.');
      }
    } finally {
      setIsCheckingConnection(false);
    }
  };
  const handleRetry = () => {
    console.log('\n🔄 User requested retry');
    setQrCode(null);
    setError(null);
    setIsAutoRetrying(false);
    const suffix = Math.random().toString(36).slice(2, 5);
    const newName = `${instanceName}${suffix}`;
    setEffectiveInstanceName(newName);
    initializeInstance(newName);
  };
  return <>
      {showConfetti && <Confetti data-id="element-577" />}

      <div className="space-y-6" data-id="element-578">
        <div className="text-center space-y-2" data-id="element-579">
          <motion.div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${error ? 'bg-red-100' : isConnected ? 'bg-green-100' : 'bg-purple-100'}`} animate={isConnected ? {
          scale: [1, 1.2, 1],
          rotate: [0, 360]
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-580">
            {error ? <AlertCircle className="w-8 h-8 text-red-600" data-id="element-581" /> : isConnected ? <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-582" /> : <QrCode className="w-8 h-8 text-[#4f1964]" data-id="element-583" />}
          </motion.div>

          <AnimatePresence mode="wait" data-id="element-584">
            {isConnected ? <motion.div key="connected" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-585">
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-586">
                  {isRTL ? '🎉 تم الربط بنجاح!' : '🎉 Successfully Connected!'}
                </h2>
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring'
            }} className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full border border-green-200" data-id="element-587">
                  <Sparkles className="w-4 h-4 text-green-600" data-id="element-588" />
                  <span className="text-green-700 font-medium text-sm" data-id="element-589">
                    {isRTL ? 'متصل' : 'Connected'}
                  </span>
                </motion.div>
              </motion.div> : <motion.div key="scanning" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} data-id="element-590">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-591">
                  {error ? isRTL ? 'حدث خطأ' : 'Error Occurred' : isRTL ? 'امسح رمز QR' : 'Scan QR Code'}
                </h2>
                <p className="text-gray-500" data-id="element-592">
                  {error || (isRTL ? 'افتح واتساب على هاتفك وامسح الرمز للربط' : 'Open WhatsApp on your phone and scan to link')}
                </p>
              </motion.div>}
          </AnimatePresence>
        </div>

        <div className="mt-8" data-id="element-593">
          {isLoading ? <div className="flex flex-col items-center justify-center py-12" data-id="element-594">
              <Loader2 className="w-12 h-12 text-[#4f1964] animate-spin mb-4" data-id="element-595" />
              <p className="text-gray-500 mb-2" data-id="element-596">
                {isRTL ? 'جاري إنشاء رمز QR...' : 'Generating QR Code...'}
              </p>
              {currentAttempt > 0 && <p className="text-xs text-gray-400" data-id="element-597">
                  {isRTL ? `المحاولة ${currentAttempt} من ${MAX_RETRIES}` : `Attempt ${currentAttempt} of ${MAX_RETRIES}`}
                </p>}
            </div> : error ? <div className="text-center py-8" data-id="element-598">
              <div className={`border rounded-lg p-4 mb-4 ${isAutoRetrying ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`} data-id="element-599">
                <div className="flex items-center justify-center gap-2 mb-1" data-id="element-600">
                  {isAutoRetrying && <Loader2 className="w-4 h-4 text-yellow-600 animate-spin" data-id="element-601" />}
                  <p className={`text-sm font-medium ${isAutoRetrying ? 'text-yellow-800' : 'text-red-800'}`} data-id="element-602">
                    {error}
                  </p>
                </div>
              </div>

              {!isAutoRetrying && <Button onClick={handleRetry} variant="primary" fullWidth data-id="element-603">
                  {isRTL ? 'تجربة اسم آخر' : 'Try Another Name'}
                </Button>}

              <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-604">
                <p className="text-sm text-gray-600 mb-2" data-id="element-605">
                  {isRTL ? 'للتواصل مع الدعم:' : 'Contact Support:'}
                </p>
                <a href="mailto:hello@operines.ai" className="text-[#4f1964] hover:text-[#3d1350] font-medium text-sm inline-flex items-center gap-2 transition-colors" data-id="element-606">
                  <Mail className="w-4 h-4" data-id="element-607" />
                  hello@operines.ai
                </a>
              </div>
            </div> : qrCode ? <div className="space-y-4" data-id="element-608">
              <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} className="bg-white p-4 rounded-xl border-2 border-gray-200 flex items-center justify-center" data-id="element-609">
                <img src={qrCode} alt="WhatsApp QR Code" className="w-64 h-64 object-contain" onError={e => {
              console.error('❌ Image failed to load');
              console.error('Image src:', qrCode);
              console.error('Is Object URL:', qrCode?.startsWith('blob:'));
              console.error('Is Data URL:', qrCode?.startsWith('data:'));
              setError(isRTL ? 'فشل في تحميل رمز QR. يرجى المحاولة مرة أخرى.' : 'Failed to load QR code. Please try again.');
            }} onLoad={() => {
              console.log('✅ QR code image loaded successfully');
              console.log('Image type:', qrCode?.startsWith('blob:') ? 'Object URL' : 'Data URL');
            }} data-id="element-610" />
              </motion.div>

              <AnimatePresence data-id="element-611">
                {isConnected && <motion.div initial={{
              opacity: 0,
              scale: 0.9,
              y: -10
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.9,
              y: -10
            }} className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-612">
                    <div className="flex items-center justify-center gap-2" data-id="element-613">
                      <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-614" />
                      <p className="text-green-700 font-medium" data-id="element-615">
                        {isRTL ? '✅ تم الاتصال بنجاح!' : '✅ Connected Successfully!'}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {!isConnected && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3" data-id="element-616">
                  <p className="text-sm text-gray-700 text-center font-medium" data-id="element-617">
                    {isRTL ? 'خطوات الربط:' : 'Connection Steps:'}
                  </p>
                  <ol className={`text-sm text-gray-600 space-y-2 ${isRTL ? 'text-right' : 'text-left'}`} data-id="element-618">
                    <li className="flex items-start gap-2" data-id="element-619">
                      <span className="flex-shrink-0 w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold" data-id="element-620">
                        1
                      </span>
                      <span data-id="element-621">
                        {isRTL ? 'افتح واتساب على هاتفك' : 'Open WhatsApp on your phone'}
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-622">
                      <span className="flex-shrink-0 w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold" data-id="element-623">
                        2
                      </span>
                      <span data-id="element-624">
                        {isRTL ? 'اذهب إلى: الإعدادات ← الأجهزة المرتبطة' : 'Go to: Settings → Linked Devices'}
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-625">
                      <span className="flex-shrink-0 w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold" data-id="element-626">
                        3
                      </span>
                      <span data-id="element-627">
                        {isRTL ? 'اضغط على "ربط جهاز" وامسح الرمز أعلاه' : 'Tap "Link a Device" and scan the code above'}
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-628">
                      <span className="flex-shrink-0 w-5 h-5 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold" data-id="element-629">
                        4
                      </span>
                      <span className="font-medium text-purple-700" data-id="element-630">
                        {isRTL ? 'بعد المسح، اضغط على "تم الربط والمتابعة" أدناه' : 'After scanning, click "Connected & Continue" below'}
                      </span>
                    </li>
                  </ol>
                </div>}

              {/* Continue Button - Shows when connected */}
              <AnimatePresence data-id="element-631">
                {isConnected ? <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 10
            }} transition={{
              delay: 0.2
            }} data-id="element-632">
                    <Button onClick={onFinish} variant="primary" fullWidth size="lg" className="shadow-lg bg-green-600 hover:bg-green-700" data-id="element-633">
                      {isRTL ? 'متابعة' : 'Continue'}
                      <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-634" />
                    </Button>
                  </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} data-id="element-635">
                    <Button onClick={onFinish} variant="primary" fullWidth size="lg" className="shadow-lg" data-id="element-636">
                      {isRTL ? 'تم الربط والمتابعة' : 'Connected & Continue'}
                      <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-637" />
                    </Button>
                  </motion.div>}
              </AnimatePresence>
            </div> : null}
        </div>

        {!isConnected && !isLoading && <div className="pt-4" data-id="element-638">
            <Button type="button" variant="secondary" onClick={onBack} fullWidth data-id="element-639">
              {isRTL ? 'رجوع' : 'Back'}
            </Button>
          </div>}
      </div>
    </>;
}